interface PaymentSessionTransactionsViewControllerDelegate
{
    sessionTransactionsDidChangeIssues( controller:PaymentSessionTransactionsViewController, sessionID:string, issues:PaymentSessionIssues ):void;
    /// A payout was generated ( a review resolved, or a pending one
    /// re-checked ): the split may have added rows the session queries do
    /// not cover - the pocket - so the list refetches itself.
    sessionTransactionsDidGeneratePayout( controller:PaymentSessionTransactionsViewController, sessionID:string ):void;
}

/// Per-transaction review of one session's payout reconcile: the venue lines
/// and the registered payment rows matched up, the wrong ones first, so the
/// user can FIND the transactions behind a held ( review ) payout and give
/// each one its payout action. Once all of them have one, completing the
/// review re-checks the session: when it reconciles the payout is generated
/// and the installment leaves review.
/// A refund changes the registered net, so after it lands the payout total
/// converges on the next re-check; a transferred payout is locked and the
/// server refuses refunds from this flow.
class PaymentSessionTransactionsViewController extends MUIViewController
{
    static newInstance() : PaymentSessionTransactionsViewController {
        let vc = new PaymentSessionTransactionsViewController("payment-session-transactions-view");
        vc.initWithResource("layout/paymenttransactions/PaymentSessionTransactionsView.html");
        return vc;
    }

    // The window fits the browser's when it is narrower
    get preferredContentSize(){
        return new MIOSize( Math.min( 1000, window.innerWidth - 40 ), Math.min( 700, window.innerHeight - 40 ) );
    }

    delegate:PaymentSessionTransactionsViewControllerDelegate = null;

    private closeButton:MUIButton = null;
    private completeButton:MUIButton = null;
    private titleLabel:MUILabel = null;
    private amountLabel:MUILabel = null;
    private dueDateLabel:MUILabel = null;
    private transactionsLabel:MUILabel = null;
    private statusLabel:MUILabel = null;
    private tableView:UITableView = null;

    viewDidLoad(){
        super.viewDidLoad();

        this.closeButton = MUIOutlet(this, "close-btn", "MUIButton");
        this.closeButton.setAction(this, function(this:PaymentSessionTransactionsViewController){
            this.dismissViewController(true);
        });

        this.completeButton = MUIOutlet(this, "complete-btn", "MUIButton");
        this.completeButton.setAction(this, function(this:PaymentSessionTransactionsViewController){
            this.completeReview();
        });

        this.titleLabel = MUIOutlet(this, "title-lbl", "MUILabel");
        this.amountLabel = MUIOutlet(this, "amount-lbl", "MUILabel");
        this.dueDateLabel = MUIOutlet(this, "due-date-lbl", "MUILabel");
        this.transactionsLabel = MUIOutlet(this, "transactions-lbl", "MUILabel");
        this.statusLabel = MUIOutlet(this, "status-lbl", "MUILabel");

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
    }

    viewWillAppear(animate?){
        super.viewWillAppear(animate);
        this.updateUI();
    }

    private _installment:AdyenProxyPaymentInstallment = null;
    private _session_id:string = null;
    private _concept:string = null;

    /// From the installments list: the installment held for review
    set installment( installment:AdyenProxyPaymentInstallment ) {
        this._installment = installment;
        this.setSession( installment?.sessionID, installment?.concept );
    }

    /// From the payment transactions: a session, its payout maybe not
    /// generated yet. The installment, when there is one, is looked up.
    setSession( sessionID:string, concept:string ) {
        this._session_id = sessionID;
        this._concept = concept;
        this._entries = [];
        this._locked = false;
        this._loaded = false;
        this._server_installment = null;
        this.reloadData();
    }

    private _entries:any[] = [];
    private _locked = false;
    private _loaded = false;
    private _busy = false;
    private _reload_seq = 0;
    private _applied_seq = 0;
    /// { id, status, amount } of the session's installment, as the server has it
    private _server_installment:any = null;

    private reloadData(){
        if (this._session_id == null) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let session_id = this._session_id;
        let seq = ++this._reload_seq;
        ad.webService.sessionTransactions( session_id, (data:any, error:string) => {
            if (error != null) {
                if (seq == this._reload_seq) AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("SOMETHING_WENT_WRONG", "SOMETHING WENT WRONG") );
                return;
            }
            // An earlier load can land after a later one: never go back to it
            if (seq < this._applied_seq) return;
            this._applied_seq = seq;
            let result = data?.["data"] ?? data ?? {};
            this._entries = PaymentSessionTransactionsViewController.sortedEntries( result["transactions"] ?? [] );
            this._locked = result["locked"] == true;
            this._server_installment = result["installment"];
            this._loaded = true;
            this.syncInstallment();
            this.updateUI();
            this.delegate?.sessionTransactionsDidChangeIssues( this, session_id, PaymentSessionIssues.fromEntries( this._entries ) );
        });
    }

    /// The local installment: looked up when the window opened by session,
    /// refreshed when the server has it in another status ( the list's row
    /// updates with it )
    private syncInstallment() {
        let id = this._server_installment?.["id"];
        if (id == null) return;
        if (this._installment != null && this._installment.status == this.installmentStatus) return;

        DBHelper.queryObjectWithCompletion( "AdyenProxyPaymentInstallment", null, MIOPredicate.predicateWithFormat( "identifier == " + id ), [], this, function(this:PaymentSessionTransactionsViewController, object:AdyenProxyPaymentInstallment){
            if (object == null) return;
            this._installment = object;
            this.updateUI();
        });
    }

    /// The status of the session's installment, as the server has it ( the
    /// local one can be older ); null while there is no payout
    private get installmentStatus() : number {
        let status = this._server_installment?.["status"];
        if (status != null) return parseInt( status );
        return this._installment?.status ?? null;
    }

    /// A held session, or one without a payout yet: completing the review
    /// waits for an action on every wrong transaction that can take one
    private get isReviewing() : boolean {
        let status = this.installmentStatus;
        return status == PaymentInstallmentStatus.review || ( status == null && this._server_installment == null );
    }

    /// Reviewing is about giving every wrong transaction its action: the
    /// table shows ONLY them then ( decided ones included, so an action can
    /// still be changed ). Outside the review the whole session shows.
    private get visibleEntries() : any[] {
        if (this.isReviewing == false) return this._entries;
        return this._entries.filter( e => PaymentSessionIssues.isIssue( e ) );
    }

    /// The wrong transactions first, then the rest, each part by date
    private static sortedEntries( entries:any[] ) : any[] {
        let time = (e:any) => e["date"] != null ? new Date( e["date"] ).getTime() : 0;
        return entries.map( (e, i) => ({ e, i }) ).sort( (a, b) => {
            let issue_a = PaymentSessionIssues.isIssue( a.e ) ? 0 : 1;
            let issue_b = PaymentSessionIssues.isIssue( b.e ) ? 0 : 1;
            if (issue_a != issue_b) return issue_a - issue_b;
            return ( time( a.e ) - time( b.e ) ) || ( a.i - b.i );
        }).map( x => x.e );
    }

    private updateUI(){
        if (this.viewIsLoaded == false) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let installment = this._installment;
        let issues = PaymentSessionIssues.fromEntries( this._entries );

        this.titleLabel.text = ReconciliationEntryStateHelper.escape( this._concept ?? MIOLocalizeString("SESSION_TRANSACTIONS", "SESSION TRANSACTIONS") );

        let amount = this._server_installment?.["amount"] != null ? parseFloat( this._server_installment["amount"] ) : installment?.amount;
        this.amountLabel.text = amount != null ? ad.currencyFormatter.stringFromNumber( amount ) : "";
        this.dueDateLabel.text = installment?.dueDate != null ? ad.dateFormatter.stringFromDate( installment.dueDate ) : "";
        this.transactionsLabel.text = this._loaded ? this.transactionsString( issues ) : "";
        this.updateStatusLabel();

        // Completing the review re-checks the payout of a held session ( or
        // generates it, when there is none yet ). A pending payout can be
        // re-checked any time, to fold in late transactions and refunds.
        let reviewing = this.isReviewing;
        let recheck = this.installmentStatus == PaymentInstallmentStatus.pending;
        let blocked = reviewing && issues.undecided > 0;
        this.completeButton.hidden = !( this._loaded && this._locked == false && ( reviewing || recheck ) );
        this.completeButton.title = reviewing ? MIOLocalizeString("REVIEW_COMPLETED", "REVIEW COMPLETED") : MIOLocalizeString("RE_CHECK_SESSION", "RE-CHECK SESSION");
        this.setButtonEnabled( this.completeButton, !blocked && this._busy == false );
        this.completeButton.layer.title = blocked ? MIOLocalizeString("ASSIGN_AN_ACTION_TO_EVERY_ISSUE", "ASSIGN AN ACTION TO EVERY ISSUE") : "";

        // The rows rebuild with the action dropdown just used still focused: keep the scroll
        let scroll_top = this.tableView.layer.scrollTop;
        this.tableView.reloadData();
        this.tableView.layer.scrollTop = scroll_top;
    }

    // "5 · 1 with issues", or "5 · 2 reviewed" once they all have an action
    private transactionsString( issues:PaymentSessionIssues ) : string {
        let text = this._entries.length.toString();
        if (issues.pending > 0) {
            return text + " · " + PaymentInstallmentDueHeader.countString( issues.pending, "ONE_WITH_ISSUE", "1 WITH ISSUE", "N_WITH_ISSUES", "%d WITH ISSUES" );
        }
        if (issues.reviewed > 0) {
            return text + " · " + PaymentInstallmentDueHeader.countString( issues.reviewed, "ONE_REVIEWED_ISSUE", "1 REVIEWED", "N_REVIEWED_ISSUES", "%d REVIEWED" );
        }
        return text;
    }

    private updateStatusLabel() {
        let installment = this._installment;
        let status = this.installmentStatus;
        for (let style of [ "alert", "green", "pst-muted" ]) MUICoreLayerRemoveStyle( this.statusLabel.layer, style );

        if (status == null) {
            this.statusLabel.text = this._loaded ? MIOLocalizeString("NO_PAYOUT_GENERATED", "NO PAYOUT GENERATED YET") : "";
            MUICoreLayerAddStyle( this.statusLabel.layer, "pst-muted" );
            return;
        }

        switch (status) {
            case PaymentInstallmentStatus.review:
                this.statusLabel.text = PaymentInstallment.statusStringFor( status );
                MUICoreLayerAddStyle( this.statusLabel.layer, "alert" );
            break;
            case PaymentInstallmentStatus.paid:
                this.statusLabel.text = installment?.status == status ? InstallmentCell.paidStatusString( installment ) : PaymentInstallment.statusStringFor( status );
                MUICoreLayerAddStyle( this.statusLabel.layer, "green" );
            break;
            default:
                this.statusLabel.text = PaymentInstallment.statusStringFor( status );
            break;
        }
    }

    private setButtonEnabled( button:MUIButton, enabled:boolean ) {
        button.enabled = enabled;
        if (enabled) MUICoreLayerRemoveStyle( button.layer, "disable" );
        else MUICoreLayerAddStyle( button.layer, "disable" );
    }

    numberOfSections(tableView:UITableView){ return 1; }
    numberOfRowsInSection(tableView:UITableView, section:number){ return this.visibleEntries.length; }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let cell = tableView.dequeueReusableCellWithIdentifier("PaymentSessionTransactionCell") as PaymentSessionTransactionCell;
        cell.owner = this;
        cell.entry = this.visibleEntries[ indexPath.row ];
        return cell;
    }

    refundEntry( entry:any ) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let amount = ad.currencyFormatter.stringFromNumber( parseFloat( ( entry["venueAmount"] ?? entry["dlAmount"] ) ?? "0" ) );

        let avc = new MUIAlertViewController();
        avc.initWithTitle( MIOLocalizeString("REFUND", "REFUND"), MIOLocalizeString("REFUND_TRANSACTION_CONFIRMATION", "REFUND THIS TRANSACTION TO THE CUSTOMER?") + " " + amount, MUIAlertViewStyle.Default );

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("REFUND", "REFUND"), MUIAlertActionStyle.Destructive, this, function(this:PaymentSessionTransactionsViewController){
            this.doRefund( entry );
        }));
        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null ) );

        this.presentViewController( avc, true );
    }

    private doRefund( entry:any ) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.refundTransaction( entry["venueTransactionID"], (data:any, error:string) => {
            if (error != null) {
                AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), error );
                return;
            }
            // The refund is confirmed asynchronously: the ledger registers it
            // through the platform notification, usually within seconds.
            AppHelper.showInfoMessage( null, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("REFUND_SENT", "THE REFUND WAS SENT. IT REGISTERS ON THE PAYOUT IN A FEW SECONDS - RE-CHECK THE SESSION AFTERWARDS.") );
            this.reloadData();
        });
    }

    /// The payout action of a wrong transaction: "pocket", "transfer", or
    /// "pending" to take it back
    setEntryAction( entry:any, action:string ) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.setTransactionAction( entry["dlTransactionID"], action, (data:any, error:string) => {
            if (error != null) {
                AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), error );
            }
            // Either way the rows show what the server has
            this.reloadData();
        });
    }

    /// Re-runs the payout of the session: with every wrong transaction
    /// pocketed or transferred it reconciles, the payout is generated and
    /// the installment leaves review. On a pending payout it folds in the
    /// transactions registered since.
    private completeReview() {
        if (this._busy || this._session_id == null) return;
        this._busy = true;
        this.updateUI();

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let session_id = this._session_id;
        let reviewing = this.isReviewing;
        ad.webService.processSessionPayout( session_id, (data:any, error:string) => {
            this._busy = false;
            if (error != null) {
                AppHelper.showErrorMessage( null, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("SOMETHING_WENT_WRONG", "SOMETHING WENT WRONG") );
                this.updateUI();
                return;
            }

            // The venue installment changed server side ( amounts or status ):
            // pull it into the local cache so the installments screen updates.
            DBHelper.queryObjectsWithCompletion( "AdyenProxyPaymentInstallment", null, MIOPredicate.predicateWithFormat( "sessionID == " + session_id ), [], this, function(this:PaymentSessionTransactionsViewController){
                this.updateUI();
            });

            let result = data?.["data"]?.["result"];
            switch (result) {
                case "generated":
                    AppHelper.showInfoMessage( null, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("SESSION_RECONCILED", "THE SESSION RECONCILED AND THE PAYOUT WAS GENERATED") );
                    this.delegate?.sessionTransactionsDidGeneratePayout( this, session_id );
                    // The review is done: back to the list
                    if (reviewing) {
                        this.dismissViewController( true );
                        return;
                    }
                break;
                case "noPending":
                    AppHelper.showInfoMessage( null, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("SESSION_NO_PENDING_TRANSACTIONS", "THE SESSION HAS NO TRANSACTIONS PENDING PAYOUT") );
                break;
                case "open":
                    AppHelper.showInfoMessage( null, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("SESSION_STILL_OPEN", "THE CASH DESK SESSION IS STILL OPEN") );
                break;
                default:
                    AppHelper.showInfoMessage( null, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("SESSION_STILL_MISMATCHED", "THE SESSION STILL DOES NOT RECONCILE") );
                break;
            }
            this.reloadData();
        });
    }
}

class PaymentSessionTransactionCell extends UITableViewCell
{
    owner:PaymentSessionTransactionsViewController = null;

    private dateLabel:MUILabel = null;
    private userLabel:MUILabel = null;
    private cardLabel:MUILabel = null;
    private referenceLabel:MUILabel = null;
    private ticketLabel:MUILabel = null;
    private dlAmountLabel:MUILabel = null;
    private venueAmountLabel:MUILabel = null;
    private statusLabel:MUILabel = null;
    private actionComboBox:MUIComboBox = null;
    private refundButton:MUIButton = null;

    awakeFromHTML(): void {
        this.dateLabel = MUIOutlet( this, "date-lbl", "MUILabel" );
        this.userLabel = MUIOutlet( this, "user-lbl", "MUILabel" );
        this.cardLabel = MUIOutlet( this, "card-lbl", "MUILabel" );
        this.referenceLabel = MUIOutlet( this, "reference-lbl", "MUILabel" );
        this.ticketLabel = MUIOutlet( this, "ticket-lbl", "MUILabel" );
        this.dlAmountLabel = MUIOutlet( this, "dl-amount-lbl", "MUILabel" );
        this.venueAmountLabel = MUIOutlet( this, "venue-amount-lbl", "MUILabel" );
        this.statusLabel = MUIOutlet( this, "status-lbl", "MUILabel" );
        this.refundButton = MUIOutlet( this, "refund-btn", "MUIButton" );
        this.refundButton.setAction( this, function(this:PaymentSessionTransactionCell){
            if (this._entry != null) this.owner?.refundEntry( this._entry );
        });

        // The payout action of a wrong transaction: GO TO POCKET keeps the
        // money on the balance account out of the payout, TRANSFER accepts it
        // into the payout without a venue line covering it, and SELECT AN
        // ACTION takes the decision back.
        this.actionComboBox = MUIOutlet( this, "action-cb", "MUIComboBox" );
        this.actionComboBox.setOnChangeAction( this, function(this:PaymentSessionTransactionCell, control:MUIComboBox, value:string){
            if (this._entry != null) this.owner?.setEntryAction( this._entry, value );
        });

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private static actionOfStatus( dlStatus:number ) : string {
        switch (dlStatus) {
            case 2: return "pocket";
            case 3: return "transfer";
            default: return "pending";
        }
    }

    // "MC •••• 4821", or the brand alone without the last digits
    private static cardString( entry:any ) : string {
        let brand = ( entry["cardBrand"] ?? "" ).toString().toUpperCase();
        let summary = entry["cardSummary"];
        if (summary == null || summary.length == 0) return brand;
        return ( brand + " •••• " + summary ).trim();
    }

    private _entry:any = null;
    set entry( entry:any ) {
        this._entry = entry;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let escape = ReconciliationEntryStateHelper.escape;

        let date = entry["date"] != null ? new Date( entry["date"] ) : null;
        this.dateLabel.text = date != null ? ad.dateTimeFormatter.stringFromDate( date ) : "";
        this.userLabel.text = escape( entry["workerName"] );
        this.userLabel.hidden = entry["workerName"] == null;

        this.cardLabel.text = escape( PaymentSessionTransactionCell.cardString( entry ) );

        this.referenceLabel.text = escape( entry["reference"] ?? entry["venueReference"] );
        this.ticketLabel.text = MIOLocalizeString("TICKET_NUMBER", "TICKET %s").replace( "%s", escape( entry["ticket"] ) );
        this.ticketLabel.hidden = entry["ticket"] == null;

        // The registered amount; the ticket's under it when they differ or
        // nothing was registered
        let dl_amount = entry["dlAmount"] != null ? parseFloat( entry["dlAmount"] ) : null;
        let venue_amount = entry["venueAmount"] != null ? parseFloat( entry["venueAmount"] ) : null;
        this.dlAmountLabel.text = dl_amount != null ? ad.currencyFormatter.stringFromNumber( dl_amount ) : "";
        let show_venue = venue_amount != null && ( dl_amount == null || Math.abs( venue_amount - dl_amount ) > 0.01 );
        this.venueAmountLabel.text = show_venue ? MIOLocalizeString("TICKET_AMOUNT", "TICKET") + " " + ad.currencyFormatter.stringFromNumber( venue_amount ) : "";
        this.venueAmountLabel.hidden = !show_venue;

        // The wrong ones that can take a payout action show the dropdown;
        // a matched one the customer can get the money back of, the refund
        let actionable = PaymentSessionIssues.isActionable( entry );
        this.actionComboBox.hidden = !actionable;
        if (actionable) this.actionComboBox.selectItem( PaymentSessionTransactionCell.actionOfStatus( entry["dlStatus"] ) );
        this.refundButton.hidden = actionable || entry["refundable"] != true;

        this.updateStatus( entry );
    }

    private updateStatus( entry:any ) {
        let status = entry["status"];

        for (let style of [ "alert", "green", "pst-muted", "pst-strong" ]) MUICoreLayerRemoveStyle( this.statusLabel.layer, style );

        if (PaymentSessionIssues.isDecided( entry )) {
            this.statusLabel.text = MIOLocalizeString("RESOLVED", "RESOLVED");
            MUICoreLayerAddStyle( this.statusLabel.layer, "green" );
            return;
        }

        this.statusLabel.text = this.statusString( status );
        if (PaymentSessionIssues.isIssue( entry )) {
            MUICoreLayerAddStyle( this.statusLabel.layer, "alert" );
            MUICoreLayerAddStyle( this.statusLabel.layer, "pst-strong" );
        }
        else {
            MUICoreLayerAddStyle( this.statusLabel.layer, "pst-muted" );
        }
    }

    private statusString( status:string ) : string {
        switch (status) {
            case "ok": return MIOLocalizeString("CORRECT", "CORRECT");
            case "refunded": return MIOLocalizeString("REFUNDED", "REFUNDED");
            case "amountMismatch": return MIOLocalizeString("AMOUNT_MISMATCH", "AMOUNT MISMATCH");
            case "missingDL": return MIOLocalizeString("NOT_CAPTURED", "NOT CAPTURED");
            case "missingVenue": return MIOLocalizeString("TICKET_MISSING", "TICKET MISSING");
            case "noReference": return MIOLocalizeString("NO_REFERENCE", "NO REFERENCE");
            default: return ReconciliationEntryStateHelper.escape( status );
        }
    }
}
